import * as React from "react"
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { DeviceNotFoundState } from "@/components/shared/empty-states"
import { AlfredDeviceConfigView } from "@/components/alfred/alfred-device-config-view"
import { useAlfredDevice } from "@/hooks/use-alfred-devices"
import { getAlfredDeviceListOrigin } from "@/lib/alfred-device-navigation"
import { formatDateTimeWithZone } from "@/lib/utils"
import { isNotFoundError } from "@/types/api"

export function AlfredDeviceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const numericId = Number(id)
  const defaultEditing = searchParams.get("edit") === "true"
  const { data: device, error, isLoading } = useAlfredDevice(numericId)
  const listOrigin = getAlfredDeviceListOrigin(location.state)
  const backToDevicesLabel = listOrigin?.clientId
    ? `Back to ${listOrigin.clientId} devices`
    : "Back to Devices"

  const handleBackToDevices = () => {
    if (listOrigin) {
      navigate(-1)
      return
    }

    navigate("/alfred/devices")
  }

  if (!isLoading && error && isNotFoundError(error)) {
    return (
      <>
        <DashboardHeader />
        <main className="container mx-auto px-4 py-6">
          <DeviceNotFoundState
            onBack={handleBackToDevices}
            actionLabel={backToDevicesLabel}
          />
        </main>
      </>
    )
  }

  return (
    <>
      <DashboardHeader />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToDevices}
            className="text-muted-foreground hover:text-foreground hover:bg-secondary -ml-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {backToDevicesLabel}
          </Button>

          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold text-foreground">
              {isLoading ? (
                <Skeleton className="h-8 w-56 bg-muted" />
              ) : (
                device?.deviceId ?? `Device ${id}`
              )}
            </h1>
            {device ? (
              <p className="text-sm text-muted-foreground">
                Client: {device.clientId}
                {device.venueCode && ` · Venue: ${device.venueCode}`}
                {device.inheritedFromGlobal.length > 0 &&
                  ` · ${device.inheritedFromGlobal.length} field${device.inheritedFromGlobal.length !== 1 ? "s" : ""} inherited from global`}
                {` · Updated ${formatDateTimeWithZone(device.updatedAt)} by ${device.updatedBy}`}
              </p>
            ) : (
              !isLoading && (
                <p className="text-sm text-muted-foreground">Device-specific configuration</p>
              )
            )}
          </div>
        </div>

        <AlfredDeviceConfigView
          id={numericId}
          defaultEditing={defaultEditing}
          onBackToDevices={handleBackToDevices}
          backToDevicesLabel={backToDevicesLabel}
        />
      </main>
    </>
  )
}
